import {
  Box, Button, Popover, PopoverArrow, PopoverBody, PopoverCloseButton,
  PopoverContent, PopoverHeader, PopoverTrigger, Portal, useColorModeValue,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { DateRangePicker, type RangeKeyDict } from "react-date-range";
import { FiCalendar, FiChevronDown } from "react-icons/fi";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

/** Mesmo popover + tema escuro do react-date-range usado no filtro de período
 * do SideFilter (CandidateListToolbar/HrProcesses) — reaproveita as
 * sobreposições de CSS em vez de recriar um estilo próprio. */
export const DateRangeFilter = ({ value, onChange }: { value: DateRange; onChange: (r: DateRange) => void }) => {
  const isDark = useColorModeValue(false, true);
  const popoverBg = useColorModeValue("white", "#0f2040");
  const textColor = useColorModeValue("gray.900", "white");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const inputBg = useColorModeValue("white", "#0f2040");

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button
          leftIcon={<FiCalendar />}
          rightIcon={<FiChevronDown />}
          variant="outline"
          size="sm"
          fontWeight="normal"
          color={textColor}
          borderColor={inputBorder}
          bg={inputBg}
        >
          {format(value.startDate, "dd/MM/yyyy")} - {format(value.endDate, "dd/MM/yyyy")}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent zIndex={9999} w="auto" bg={popoverBg}>
          <PopoverArrow bg={popoverBg} />
          <PopoverHeader color={textColor}>Selecione o período</PopoverHeader>
          <PopoverCloseButton />
          <PopoverBody p={0} sx={isDark ? {
            ".rdrDateRangeWrapper, .rdrCalendarWrapper": { background: "#0f2040" },
            ".rdrDefinedRangesWrapper": { background: "#0a1628", borderColor: "#1a3a5c" },
            ".rdrStaticRange": { background: "#0a1628", borderColor: "#1a3a5c", color: "#e8edf2" },
            ".rdrStaticRange:hover .rdrStaticRangeLabel, .rdrStaticRange:focus .rdrStaticRangeLabel": { background: "#112240" },
            ".rdrStaticRangeLabel": { color: "#e8edf2" },
            ".rdrStaticRangeSelected .rdrStaticRangeLabel": { color: "#009efb" },
            ".rdrInputRanges": { background: "#0a1628" },
            ".rdrInputRange": { color: "#e8edf2" },
            ".rdrInputRangeInput": { background: "#0f2040", borderColor: "#1a3a5c", color: "#e8edf2" },
            ".rdrDateDisplayWrapper": { background: "#0a1628" },
            ".rdrDateDisplay": { background: "#0a1628" },
            ".rdrDateDisplayItem": { background: "#0f2040", borderColor: "#1a3a5c", boxShadow: "none" },
            ".rdrDateDisplayItem input": { color: "#e8edf2" },
            ".rdrMonth": { background: "#0f2040" },
            ".rdrMonthAndYearWrapper": { background: "#0f2040" },
            ".rdrMonthAndYearPickers": { color: "#e8edf2" },
            ".rdrMonthAndYearPickers select": { background: "#0a1628", color: "#e8edf2" },
            ".rdrWeekDay": { color: "#718096" },
            ".rdrDayNumber span": { color: "#e8edf2 !important" },
            ".rdrDayPassive .rdrDayNumber span": { color: "#4a5568 !important" },
            ".rdrDayDisabled": { background: "#0a1628 !important" },
            ".rdrDayDisabled .rdrDayNumber span": { color: "#4a5568 !important" },
            ".rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span": { color: "#fff !important" },
            ".rdrDayToday .rdrDayNumber span:after": { background: "#009efb" },
            ".rdrNextPrevButton": { background: "#0a1628" },
            ".rdrNextPrevButton:hover": { background: "#112240" },
            ".rdrPprevButton i": { borderColor: "transparent #e8edf2 transparent transparent" },
            ".rdrNextButton i":  { borderColor: "transparent transparent transparent #e8edf2" },
            ".rdrMonthName": { color: "#e8edf2" },
          } : {}}>
            <Box>
              <DateRangePicker
                ranges={[{ ...value, key: "selection" }]}
                onChange={(item: RangeKeyDict) => {
                  const sel = item.selection;
                  if (sel.startDate && sel.endDate) onChange({ startDate: sel.startDate, endDate: sel.endDate });
                }}
                moveRangeOnFirstSelection={false}
                maxDate={new Date()}
                months={1}
                direction="horizontal"
              />
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
